import React from 'react';

import { formatValueByUnit } from 'src/utils/format-number';

interface GaugeChartProps {
  percent: number; // 0-100
  title: string | { main: string; code?: string };
  width?: number; // 默认200
  wrapWidth?: string;
  isNegative?: boolean; // 是否为负指标
  isEmpty?: boolean;
}

/**
 * 仪表盘环形进度条，样式与用户图片一致
 */
const GaugeChart: React.FC<GaugeChartProps> = ({
  percent,
  title,
  width = 200,
  wrapWidth,
  isNegative = false,
  isEmpty = false,
}) => {
  const displayPercent = percent;
  // 角度相关
  const startAngle = 135; // 起始角度
  const endAngle = 405; // 终止角度
  const radius = width / 2;
  const strokeWidth = 12;
  const center = width / 2;
  const r = radius - strokeWidth / 2;
  // 百分比对应的角度
  const percentAngle =
    displayPercent > 100
      ? 405
      : displayPercent < 0
        ? 135
        : startAngle + (endAngle - startAngle) * (displayPercent / 100);

  // 计算中心圆和字体大小
  const centerCircleRadius = r - 20;
  const fontSize = Math.min(width * 0.18, centerCircleRadius * 0.45); // 字体大小不超过圆圈半径的80%

  // 生成弧路径
  function describeArc(cx: number, cy: number, r: number, start: number, end: number) {
    const startRad = (Math.PI / 180) * start;
    const endRad = (Math.PI / 180) * end;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const largeArcFlag = end - start <= 180 ? 0 : 1;
    return [`M ${x1} ${y1}`, `A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2}`].join(' ');
  }

  // 分段颜色
  const sections = isNegative
    ? [
        { from: 135, to: 202, color: '#3CC47C' }, // 深绿
        { from: 202, to: 270, color: '#77d6a3' }, // 绿
        { from: 270, to: 337, color: '#FFB133' }, // 橙
        { from: 337, to: 405, color: '#FF4D4F' }, // 红
      ]
    : [
        { from: 135, to: 202, color: '#FF4D4F' }, // 红
        { from: 202, to: 270, color: '#FFB133' }, // 橙
        { from: 270, to: 337, color: '#77d6a3' }, // 绿
        { from: 337, to: 405, color: '#3CC47C' }, // 深绿
      ];

  // 兼容老title写法
  let mainTitle = '';
  let code = '';
  if (typeof title === 'string') {
    mainTitle = title;
  } else if (title && typeof title === 'object') {
    mainTitle = title.main;
    code = title.code || '';
  }

  return (
    <div style={{ width: wrapWidth || width || 'auto', textAlign: 'center' }}>
      <svg width={width} height={width} style={{ display: 'block', margin: '0 auto' }}>
        {/* 定义箭头标记 */}
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="6" refX="7" refY="3" orient="auto">
            <polygon points="0 0, 10 3, 0 6" fill="#77d6a3" />
          </marker>
        </defs>
        {/* 背景灰弧 */}
        <path
          d={describeArc(center, center, r, startAngle, endAngle)}
          stroke="#F3F3F3"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* 彩色分段弧 */}
        {sections.map((sec, i) => (
          <path
            key={i}
            d={describeArc(center, center, r, sec.from + 1, sec.to - 1)}
            stroke={sec.color}
            strokeWidth={strokeWidth}
            fill="none"
          />
        ))}
        {/* 指针 */}
        <g>
          {/* 指针线 */}
          <line
            x1={center}
            y1={center}
            x2={center + (r - 15) * Math.cos((percentAngle * Math.PI) / 180)}
            y2={center + (r - 15) * Math.sin((percentAngle * Math.PI) / 180)}
            stroke="#77d6a3"
            strokeWidth={2.5}
            markerEnd="url(#arrowhead)"
          />
        </g>
        {/* 中心白圆 */}
        <circle cx={center} cy={center} r={centerCircleRadius} fill="#F4F4F4" />
        {/* 百分比文字 */}
        <text
          x={center}
          y={center + fontSize * 0.4}
          textAnchor="middle"
          fontSize={fontSize}
          fontWeight="bold"
          fill={isNegative ? '#FF4D4F' : '#444'}
        >
          {formatValueByUnit(isEmpty ? undefined : displayPercent, '%')}
        </text>
      </svg>
      <div style={{ marginTop: 4 }}>
        {/* {code && (
          <div style={{ fontSize: width * 0.11, color: '#999', lineHeight: 1.1, marginBottom: 3 }}>{code}</div>
        )} */}
        <div style={{ fontWeight: 700, fontSize: width * 0.13, color: '#525866', lineHeight: 1 }}>
          {mainTitle}
        </div>
      </div>
    </div>
  );
};

export default GaugeChart;
